// /marriage/$nationality/in/$location — Sprint 5 Combo
// 40 nationalities × ~22 marriage-friendly districts/provinces = ~880 long-tail pages
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { MARRIAGE_NATIONALITIES, getMarriageNationality } from "@/data/marriage-nationalities";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA, FaqAccordion } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import { RelatedComparisons } from "@/components/RelatedComparisons";
import { MarriageNextStepLinks } from "@/components/MarriageNextStepLinks";
import { MarriageEmbassyLinks } from "@/components/MarriageEmbassyLinks";
import { Heart, ShieldCheck, BadgeCheck, FileCheck2, Clock, Phone, MessageCircle, MapPin, ArrowRight } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

// Curated marriage-friendly district/province registries
// (เลือกอำเภอที่นิยมจดทะเบียนสมรสกับคู่สมรสต่างชาติ — รับเอกสารต่างประเทศคล่อง)
export interface MarriageVenue {
  slug: string;
  nameTh: string;       // เขต/อำเภอ
  region: string;       // กรุงเทพฯ, ภาคใต้, ฯลฯ
  amphoe: string;       // ที่ว่าการอำเภอ/เขต
  highlights: string[]; // จุดเด่น
}

export const MARRIAGE_VENUES: MarriageVenue[] = [
  // กรุงเทพฯ — เขตยอดนิยมของคู่สมรสต่างชาติ
  { slug: "bangrak",    nameTh: "บางรัก",     region: "กรุงเทพมหานคร", amphoe: "สำนักงานเขตบางรัก", highlights: ["ชื่อมงคล 'รักแห่งบาง'", "ใกล้สถานทูตหลายประเทศ (สีลม-สาทร)", "ผู้บริหารพูดอังกฤษ"] },
  { slug: "watthana",   nameTh: "วัฒนา",       region: "กรุงเทพมหานคร", amphoe: "สำนักงานเขตวัฒนา", highlights: ["ใกล้สถานทูตอเมริกัน-อังกฤษ-ยุโรป", "นัดล่วงหน้าได้", "ย่าน Asoke / Phrom Phong"] },
  { slug: "pathumwan",  nameTh: "ปทุมวัน",     region: "กรุงเทพมหานคร", amphoe: "สำนักงานเขตปทุมวัน", highlights: ["ใจกลางเมือง", "ใกล้ MFA แจ้งวัฒนะ (รถ 30 นาที)", "ห้องจดทะเบียนแยก"] },
  { slug: "khlongtoei", nameTh: "คลองเตย",     region: "กรุงเทพมหานคร", amphoe: "สำนักงานเขตคลองเตย", highlights: ["ใกล้สถานทูตเยอรมัน-ออสเตรเลีย", "Q-system ไม่ต้องคิว", "ที่จอดรถสะดวก"] },
  { slug: "huaykwang",  nameTh: "ห้วยขวาง",    region: "กรุงเทพมหานคร", amphoe: "สำนักงานเขตห้วยขวาง", highlights: ["ใกล้สถานทูตจีน-เกาหลี", "ผู้บริหารพูดจีน/อังกฤษ", "เปิด ส.ค.-ศ. 8:30-16:30"] },
  { slug: "phayathai",  nameTh: "พญาไท",       region: "กรุงเทพมหานคร", amphoe: "สำนักงานเขตพญาไท", highlights: ["ติด BTS อนุสาวรีย์ฯ", "เงียบ ไม่แออัด", "ใกล้โรงพยาบาลพระมงกุฎ"] },
  { slug: "ratchathewi", nameTh: "ราชเทวี",     region: "กรุงเทพมหานคร", amphoe: "สำนักงานเขตราชเทวี", highlights: ["ติด BTS ราชเทวี/พญาไท", "ใกล้เซ็นทรัลเวิลด์", "นัดได้ทั้งเช้า-บ่าย"] },
  { slug: "ladprao",    nameTh: "ลาดพร้าว",    region: "กรุงเทพมหานคร", amphoe: "สำนักงานเขตลาดพร้าว", highlights: ["ใกล้สำนักงาน NYC (ลาดพร้าว 95)", "เปิดบ่าย-ค่ำ บางวัน", "ที่จอดรถใหญ่"] },
  { slug: "bangkokyai", nameTh: "บางกอกใหญ่",  region: "กรุงเทพมหานคร", amphoe: "สำนักงานเขตบางกอกใหญ่", highlights: ["ฝั่งธนฯ", "เงียบ ไม่ต้องรอคิว", "เหมาะถ่ายภาพ"] },

  // จังหวัดท่องเที่ยว + Expat Hubs (สำคัญสำหรับคู่ต่างชาติ)
  { slug: "chiangmai",  nameTh: "เชียงใหม่",   region: "ภาคเหนือ",      amphoe: "ที่ว่าการอำเภอเมืองเชียงใหม่", highlights: ["Expat hub ภาคเหนือ", "ผู้บริหารพูดอังกฤษ-จีน", "ใกล้สนามบินเชียงใหม่"] },
  { slug: "chiangrai",  nameTh: "เชียงราย",    region: "ภาคเหนือ",      amphoe: "ที่ว่าการอำเภอเมืองเชียงราย", highlights: ["บรรยากาศภูเขา", "เหมาะถ่ายภาพ + ฮันนีมูน"] },
  { slug: "phuket",     nameTh: "ภูเก็ต",      region: "ภาคใต้",        amphoe: "ที่ว่าการอำเภอเมืองภูเก็ต", highlights: ["Expat hub อันดับ 1", "ออกใบ Eng-Translation ฉับไว", "ใกล้สถานกงสุลหลายประเทศ"] },
  { slug: "kohsamui",   nameTh: "เกาะสมุย",   region: "ภาคใต้",        amphoe: "ที่ว่าการอำเภอเกาะสมุย", highlights: ["จดบนเกาะได้", "นัด Beach Ceremony ต่อได้", "ใกล้สถานกงสุลเยอรมัน"] },
  { slug: "krabi",      nameTh: "กระบี่",      region: "ภาคใต้",        amphoe: "ที่ว่าการอำเภอเมืองกระบี่", highlights: ["ทะเลอันดามัน", "ขั้นตอนเร็วในจังหวัดเล็ก"] },
  { slug: "pattaya",    nameTh: "พัทยา",       region: "ภาคตะวันออก",   amphoe: "ที่ว่าการอำเภอบางละมุง", highlights: ["Expat hub", "ผู้บริหารคุ้นเคยเอกสารต่างชาติ", "นัดวันเดียวเสร็จ"] },
  { slug: "chonburi",   nameTh: "ชลบุรี",      region: "ภาคตะวันออก",   amphoe: "ที่ว่าการอำเภอเมืองชลบุรี", highlights: ["ใกล้นิคมญี่ปุ่น-เกาหลี", "ประสบการณ์จดต่างชาติสูง"] },
  { slug: "rayong",     nameTh: "ระยอง",       region: "ภาคตะวันออก",   amphoe: "ที่ว่าการอำเภอเมืองระยอง", highlights: ["EEC", "เหมาะคู่นักลงทุน/วิศวกร"] },
  { slug: "huahin",     nameTh: "หัวหิน",      region: "ภาคตะวันตก",    amphoe: "ที่ว่าการอำเภอหัวหิน", highlights: ["รีสอร์ทเงียบสงบ", "นิยมในกลุ่มผู้เกษียณ"] },
  { slug: "nonthaburi", nameTh: "นนทบุรี",     region: "ภาคกลาง",       amphoe: "ที่ว่าการอำเภอเมืองนนทบุรี", highlights: ["ใกล้ MFA แจ้งวัฒนะ", "ทำ MFA ต่อได้วันเดียว"] },
  { slug: "pathumthani", nameTh: "ปทุมธานี",   region: "ภาคกลาง",       amphoe: "ที่ว่าการอำเภอเมืองปทุมธานี", highlights: ["ใกล้สนามบินดอนเมือง", "เหมาะคู่บินมาจดวันเดียว"] },
  { slug: "samutprakan", nameTh: "สมุทรปราการ", region: "ภาคกลาง",      amphoe: "ที่ว่าการอำเภอเมืองสมุทรปราการ", highlights: ["ใกล้สุวรรณภูมิ", "นิยมในกลุ่ม Expat Bang Na"] },
  { slug: "udonthani",  nameTh: "อุดรธานี",    region: "ภาคอีสาน",      amphoe: "ที่ว่าการอำเภอเมืองอุดรธานี", highlights: ["Expat hub ภาคอีสาน", "คู่ครองครึ่งหนึ่งจากต่างประเทศ"] },
];

export function getMarriageVenue(slug: string) {
  return MARRIAGE_VENUES.find((v) => v.slug === slug);
}

export const Route = createFileRoute("/marriage/$nationality/in/$location")({
  loader: ({ params }) => {
    const n = getMarriageNationality(params.nationality);
    const v = getMarriageVenue(params.location);
    if (!n || !v) throw notFound();
    return { nationality: params.nationality, location: params.location };
  },
  head: ({ params }) => {
    const n = getMarriageNationality(params.nationality);
    const v = getMarriageVenue(params.location);
    if (!n || !v) return { meta: [{ title: ctaTitle("ไม่พบหน้านี้", "/marriage/$nationality/in/$location", "th")}] };
    const url = `${SITE}/marriage/${n.slug}/in/${v.slug}`;
    const title = `จดทะเบียนสมรสกับ${n.nameTh} ${n.flag} ที่${v.nameTh} เริ่ม ฿${n.serviceFeeTh.toLocaleString()} | NYC`;
    const desc = `จดทะเบียนสมรสกับ${n.nameTh} (${n.countryEn}) ที่${v.amphoe} ${v.nameTh} — บริการครบจบ: สถานทูต → แปลไทย → MFA → นัดอำเภอ เสร็จ ${n.turnaround} — ปรึกษาก่อนยื่นจริง ลดโอกาสเอกสารตีกลับ`;
    return {
      meta: [
        { title: ctaTitle(title, "/marriage/$nationality/in/$location", "th") }, { name: "description", content: ctaDesc(desc, "/marriage/$nationality/in/$location", "th")},
        { name: "keywords", content: [...n.keywords, `จดทะเบียนสมรส ${v.nameTh}`, `marry ${n.countryEn} in ${v.nameTh}`].join(", ") },
        { property: "og:title", content: ctaTitle(title, "/marriage/$nationality/in/$location", "th")}, { property: "og:description", content: ctaDesc(desc, "/marriage/$nationality/in/$location", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/marriage/${n.slug}/in/${v.slug}`, `/en/marriage/${n.slug}/in/${v.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: `จดทะเบียนสมรสกับ${n.nameTh}ที่${v.nameTh}`,
              serviceType: `Marriage Registration for ${n.countryEn} at ${v.nameTh}`,
              areaServed: [{ "@type": "AdministrativeArea", name: v.nameTh }, { "@type": "Country", name: n.countryEn }],
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", url: SITE, telephone: "+66832494999" },
              offers: { "@type": "Offer", price: n.serviceFeeTh, priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "FAQPage", "@id": `${url}#faq`,
              mainEntity: [
                { "@type": "Question", name: `จดทะเบียนสมรสกับ${n.nameTh}ที่${v.nameTh}ได้ไหม?`, acceptedAnswer: { "@type": "Answer", text: `ได้ครับ — ${v.amphoe} รับจดทะเบียนสมรสคู่ต่างชาติ-ไทย รวมถึงสัญชาติ${n.nameTh} โดย NYC จัดเตรียม Affidavit + แปล + MFA + นัดให้ครบ` } },
                { "@type": "Question", name: `ใช้เวลาเท่าไหร่?`, acceptedAnswer: { "@type": "Answer", text: `รวม Affidavit + แปล + MFA + นัด${v.amphoe} ใช้เวลาประมาณ ${n.turnaround} มี Express 3 วันค่าเร่ง +฿3,500` } },
                ...n.faqs.slice(0, 2).map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
              ],
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "จดทะเบียนสมรส", item: `${SITE}/marriage` },
                { "@type": "ListItem", position: 3, name: n.nameTh, item: `${SITE}/marriage/${n.slug}` },
                { "@type": "ListItem", position: 4, name: v.nameTh, item: url },
              ],
            }),
          },
          ...reviewNodes(url, `${url}#service`, 2).map((r) => ({ type: "application/ld+json" as const, children: JSON.stringify(r) })),
        ]),
      ],
    };
  },
  component: MarriageVenuePage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">ไม่พบหน้านี้</h1>
      <Link to="/marriage" className="mt-4 inline-block text-gold underline">กลับไปยังจดทะเบียนสมรส</Link>
          <QuotableStatsStrip cluster="marriage" locale="th" variant="light" />
    </main>
  ),
});

function MarriageVenuePage() {
  const { nationality, location } = Route.useLoaderData() as { nationality: string; location: string };
  const n = getMarriageNationality(nationality)!;
  const v = getMarriageVenue(location)!;
  const otherVenues = MARRIAGE_VENUES.filter((x) => x.slug !== location && x.region === v.region).slice(0, 6);
  const otherNats = MARRIAGE_NATIONALITIES.filter((x) => x.slug !== nationality && x.region === n.region).slice(0, 6);

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/marriage" className="hover:text-gold">จดทะเบียนสมรส</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/marriage/$nationality" params={{ nationality }} className="hover:text-gold">{n.nameTh}</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{v.nameTh}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Heart className="h-3.5 w-3.5" /> Marriage · {n.countryEn} × {v.nameTh}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            <span className="mr-3 text-4xl">{n.flag}</span>
            จดทะเบียนสมรสกับ{n.nameTh}ที่{v.nameTh}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            บริการแบบ One-Stop — พาไปสถานทูตขอ Affidavit + แปลไทยโดยนักแปลขึ้นทะเบียน MFA + รับรองนิติกรณ์ + นัด{v.amphoe} ครบทุกขั้นตอน
          </p>

          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "ราคาเริ่ม", v: `฿${n.serviceFeeTh.toLocaleString()}` },
              { k: "เสร็จภายใน", v: n.turnaround },
              { k: "อำเภอ", v: v.nameTh },
              { k: "Rating", v: "4.9 / 2,147" },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md transition hover:-translate-y-0.5 hover:bg-emerald-400">
              <MessageCircle className="h-4 w-4" /> ปรึกษาผ่าน LINE
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md transition hover:-translate-y-0.5">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable · AI Overview ready
          </span>
          NYC จัดบริการจดทะเบียนสมรสคู่{n.nameTh}-ไทยที่{v.amphoe} ({v.nameTh}) ครบทุกขั้นตอน เริ่ม ฿{n.serviceFeeTh.toLocaleString()} เสร็จ {n.turnaround} — รวมพาไป {n.singleStatusSource}, แปลไทย, รับรอง MFA, นัดอำเภอ และล่ามภาษาอังกฤษ
        </blockquote>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ทำไมต้อง{v.nameTh}?</h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {v.highlights.map((h) => (
              <li key={h} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 text-sm text-slate-800">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {h}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">เอกสารฝ่าย{n.nameTh}</h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {n.topDocs.map((d) => (
              <li key={d} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 text-sm text-slate-800">
                <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {d}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">แพ็กเกจ One-Stop</div>
          <h2 className="mt-1 font-display text-3xl font-extrabold text-navy-deep sm:text-4xl">
            จดทะเบียนกับ{n.nameTh}ที่{v.nameTh} เริ่ม ฿{n.serviceFeeTh.toLocaleString()}
          </h2>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> รวมพาไปสถานทูต + แปล + MFA + นัด{v.amphoe}</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Express 3 วันค่าเร่ง +฿3,500</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> ล่ามอำเภอ (อังกฤษ / ภาษาท้องถิ่น) +฿2,500</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> แปล คร.2/คร.3 + Apostille สำหรับใช้ต่างประเทศ</li>
          </ul>
          <div className="mt-6"><ContactCTA /></div>
        </section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">คำถามที่พบบ่อย</h2>
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-2 sm:p-3">
            <FaqAccordion items={[
              { q: `จดทะเบียนสมรสกับ${n.nameTh}ที่${v.nameTh}ได้ไหม?`, a: `ได้ครับ ${v.amphoe} รับจดทะเบียนคู่สมรสต่างชาติ NYC จัดเอกสาร Affidavit + แปล + MFA + นัดให้ครบ` },
              { q: `ใช้เวลาเท่าไหร่ และมี Express ไหม?`, a: `รวมทุกขั้นตอน ${n.turnaround} — เลือก Express 3 วันได้ +฿3,500` },
              ...n.faqs.slice(0, 2),
            ]} />
          </div>
        </section>

        {otherVenues.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">จดที่อำเภออื่นใน{v.region}</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {otherVenues.map((o) => (
                <Link key={o.slug} to="/marriage/$nationality/in/$location" params={{ nationality, location: o.slug }}
                  className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md">
                  <MapPin className="h-5 w-5 text-amber-600" />
                  <div className="flex-1">
                    <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">{o.nameTh}</div>
                    <div className="mt-0.5 text-[11px] text-slate-500">{o.amphoe}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-amber-600" />
                </Link>
              ))}
            </div>
          </section>
        )}

        {otherNats.length > 0 && (
          <section className="mt-12">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">สัญชาติอื่นที่จดที่{v.nameTh}ได้</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {otherNats.map((o) => (
                <Link key={o.slug} to="/marriage/$nationality/in/$location" params={{ nationality: o.slug, location }}
                  className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md">
                  <div className="text-2xl">{o.flag}</div>
                  <div className="flex-1">
                    <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">{o.nameTh}</div>
                    <div className="mt-0.5 text-[11px] text-slate-500">เริ่ม ฿{o.serviceFeeTh.toLocaleString()} · {o.turnaround}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      <MarriageNextStepLinks nationality={n} venueSlug={location} venueNameTh={v.nameTh} />
      <div className="mx-auto max-w-5xl px-4">
        <MarriageEmbassyLinks
          nationality={n.slug}
          nationalityTh={n.nameTh}
          countryTh={n.countryTh}
        />
      </div>
      <RelatedComparisons category="visa" locale="th" />
      <TrustLayer variant="compact" />
          <QuotableStatsStrip cluster="marriage" locale="th" variant="light" />
    </main>
  );
}
